export interface FieldValidatorResult {
    valid: boolean;
    message?: string;
}

export const validateEmail = (s: string): FieldValidatorResult => {
    const re = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

    const valid = re.test(String(s).toLowerCase());
    const message = !valid ? "Invalid email address" : undefined;

    return { valid, message };
};

export const validatePhoneNumber = (s: string): FieldValidatorResult => {
    // Nothing is valid
    if (isEmptyString(s)) {
        return { valid: true };
    }

    const valid = validateUSPhoneNumber(s);
    const message = !valid ? "Invalid phone number" : undefined;

    return { valid, message };
};

export const validateZipCode = (s: string): FieldValidatorResult => {
    // Nothing is valid
    if (isEmptyString(s)) {
        return { valid: true };
    }

    // US 5-digit: 12345
    const usZip5Regex = /^\d{5}$/;
    // US 9-digit (ZIP+4): 12345-6789
    const usZip9Regex = /^\d{5}-\d{4}$/;
    // Canadian postal code: A1A 1A1 or A1A1A1 (letter-digit-letter space digit-letter-digit, space optional)
    const canadianPostalCodeRegex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

    const valid =
        usZip5Regex.test(s) ||
        usZip9Regex.test(s) ||
        canadianPostalCodeRegex.test(s);

    const message = !valid ? "Invalid zip code or postal code" : undefined;

    return { valid, message };
};

export const isEmptyString = (s: string): boolean => {
    return typeof s !== "string" || s.trim().length === 0;
};

const validateUSPhoneNumber = (s: string) => {
    const sanitizedNumber = s.replace(/\D/g, "");

    if (
        sanitizedNumber.length === 10 ||
        (sanitizedNumber.length === 11 && sanitizedNumber.startsWith("1"))
    ) {
        const validPattern = /^(1)?[-. ]?\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/;
        return validPattern.test(sanitizedNumber);
    }

    return false;
};

export const formatUSPhoneNumber = (s: string) => {
    if (isEmptyString(s) || !validateUSPhoneNumber(s)) {
        return s;
    }

    let sanitizedNumber = s.replace(/\D/g, "");

    if (sanitizedNumber.length === 11 && sanitizedNumber.startsWith("1")) {
        sanitizedNumber = sanitizedNumber.slice(1);
    }

    const formattedNumber = sanitizedNumber.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

    return formattedNumber;
};

export const formatZipCode = (s: string): string => {
    if (isEmptyString(s)) {
        return s;
    }

    const trimmed = s.trim();

    // Check if it looks like a Canadian postal code without space
    const canadianNoSpace = /^([A-Za-z]\d[A-Za-z])(\d[A-Za-z]\d)$/;
    const match = trimmed.match(canadianNoSpace);
    if (match) {
        return `${match[1].toUpperCase()} ${match[2].toUpperCase()}`;
    }

    // Uppercase Canadian postal codes with space
    const canadianWithSpace = /^[A-Za-z]\d[A-Za-z]\s\d[A-Za-z]\d$/;
    if (canadianWithSpace.test(trimmed)) {
        return trimmed.toUpperCase();
    }

    // Return as-is for US zip codes or invalid input
    return s;
};

export const fieldErrorsEqual = (
    obj1: { [key: string]: FieldValidatorResult },
    obj2: { [key: string]: FieldValidatorResult }
): boolean => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!obj2.hasOwnProperty(key)) {
            return false;
        }

        const attr1 = obj1[key];
        const attr2 = obj2[key];

        if (attr1.valid !== attr2.valid) {
            return false;
        }
    }

    return true;
};

export const isEmpty = (obj: unknown) => {
    // Check if the value is null or undefined
    if (obj == null) {
        return true;
    }
    // Check if the value is an empty or whitespace-only string
    if (typeof obj === "string") {
        return obj.trim().length === 0;
    }
    // Check if the value is an array
    if (Array.isArray(obj)) {
        return obj.length === 0;
    }
    // Check if the value is a plain object
    if (typeof obj === "object" && obj.constructor === Object) {
        return Object.keys(obj).length === 0;
    }
    // For other types (e.g., Map, Set), check if they are empty
    if (typeof obj === "object") {
        return Object.keys(obj as object).length === 0;
    }
    // If none of the above conditions are met, return false
    return false;
};
